import './HeaderSearch.css';
import {useCallback, useEffect, useRef, useState} from "react";
import {HeaderSearchResult} from "./sub/HeaderSearchResult/HeaderSearchResult.jsx";
import {findProductsByTitleMatch} from "../../../../ProductsHandler.js";
import {useLocation, useNavigate} from "react-router-dom";
import LoadingIndicator from "../../../LoadingIndicator/LoadingIndicator.jsx";

export default function HeaderSearch() {
    const [searchString, setSearchString] = useState('');
    const [results, setResults] = useState([]);
    const [endCursor, setEndCursor] = useState(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const observer = useRef();
    const containerRef = useRef(null);

    const fetchResults = async (after = null, reset = false) => {
        if (loading || searchString.trim() === '') return;
        setLoading(true);
        const data = await findProductsByTitleMatch(searchString, after);
        setResults(prev => reset ? data.products : [...prev, ...data.products]);
        setEndCursor(data.endCursor);
        setHasNextPage(data.hasNextPage);
        setLoading(false);
    };

    useEffect(() => {
        if (searchString.trim() === '') {
            setResults([]);
            setEndCursor(null);
            setHasNextPage(false);
            return;
        }
        fetchResults(null, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchString]);

    useEffect(() => {
        setSearchString("");
        setResults([]);
        setEndCursor(null);
        setHasNextPage(false);
    }, [location]);

    const lastResultRef = useCallback(node => {
        if (loading || !hasNextPage) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                fetchResults(endCursor);
            }
        });

        if (node) observer.current.observe(node);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, hasNextPage, endCursor]);

    const search = () => {
        const q = searchString.trim();
        if (!q) return;
        navigate(`/produkty?nazwa=${encodeURIComponent(q)}`);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            search();
        } else if (e.key === 'Escape') {
            setSearchString('');
            setResults([]);
        }
    };

    // 📱 Close results + clear search on mobile when tapping outside a 30px radius
    useEffect(() => {
        if (results.length === 0) return;

        const handler = (e) => {
            const isMobile = window.matchMedia('(max-width: 768px)').matches;
            if (!isMobile) return;

            const root = containerRef.current;
            if (!root) return;

            // 1) If event originated inside the search container, ignore
            const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
            if (path.includes(root) || root.contains(e.target)) return;

            // 2) Apply the 30px radius check around the container
            const rect = root.getBoundingClientRect();
            const R = 30;
            const expanded = {
                left: rect.left - R,
                right: rect.right + R,
                top: rect.top - R,
                bottom: rect.bottom + R,
            };

            const point = e.touches?.[0] ?? e;
            const x = point.clientX;
            const y = point.clientY;

            const insideExpanded =
                x >= expanded.left &&
                x <= expanded.right &&
                y >= expanded.top &&
                y <= expanded.bottom;

            if (!insideExpanded) {
                setSearchString('');
                setResults([]);
                setEndCursor(null);
                setHasNextPage(false);
            }
        };

        // Using pointerdown was fine, but with the inside check it won't swallow clicks anymore
        document.addEventListener('pointerdown', handler, { passive: true });
        return () => document.removeEventListener('pointerdown', handler);
    }, [results.length]);

    // Helper to close & clear after picking a result
    const closeAndClear = () => {
        setSearchString('');
        setResults([]);
        setEndCursor(null);
        setHasNextPage(false);
    };

    return (
        <div id='header-search' ref={containerRef}>
            <input
                type='text'
                id='header-search-input'
                placeholder='Szukaj produktów...'
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                onKeyDown={onKeyDown}
                autoComplete="off"
                aria-label="Szukaj produktów"
            />

            {results.length > 0 && (
                <div id='header-search-result-container'>
                    {results.map((item, index) => {
                        const isLast = index === results.length - 1;
                        return (
                            <HeaderSearchResult
                                item={item}
                                key={item.id}
                                ref={isLast ? lastResultRef : null}
                                onPicked={closeAndClear}
                            />
                        );
                    })}
                    {loading && <LoadingIndicator height={3} />}
                </div>
            )}

            <div
                id='header-search-bttn'
                role="button"
                tabIndex={0}
                aria-label="Szukaj"
                title="Szukaj"
                onClick={search}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') search(); }}
            />
        </div>
    );
}
